/**
 * Magma Skin - Color schemes for Base components
 *
 * The magma skin uses player-colored surface and accent gradients.
 * Fixed elements (purple frame, dark inner quadrants, torch/corner elements) are shared.
 * Player-specific colors follow the gradient patterns from the Figma SVGs.
 */

export interface MagmaBaseColorScheme {
  name: string;
  ariaLabel: string;
  /** Main surface gradient - primary stop */
  surfacePrimary: string;
  /** Main surface gradient - secondary stop */
  surfaceSecondary: string;
  /** Decorative accent gradient - start stop */
  accentStart: string;
  /** Decorative accent gradient - mid stop (offset 0.52) */
  accentMid: string;
  /** Decorative accent gradient - end stop */
  accentEnd: string;
  /** Pawn circle gradient - start stop */
  pawnGradientStart: string;
  /** Pawn circle gradient - mid stop (offset 0.52) */
  pawnGradientMid: string;
  /** Pawn circle gradient - end stop */
  pawnGradientEnd: string;
  /** Pawn spot shadow color */
  pawnSpotShadow: string;
  /** Sparkle/splash large element color */
  sparklePrimary: string;
  /** Sparkle/splash small element color */
  sparkleSecondary: string;
}

export const MAGMA_COLOR_SCHEMES = {
  blue: {
    name: 'blue',
    ariaLabel: 'Blue player home base',
    surfacePrimary: '#89E5DE',
    surfaceSecondary: '#05D3C8',
    accentStart: '#11A39F',
    accentMid: '#0DFFFF',
    accentEnd: '#0AB9C6',
    pawnGradientStart: '#11A39F',
    pawnGradientMid: '#0DFFFF',
    pawnGradientEnd: '#0AB9C6',
    pawnSpotShadow: '#034442',
    sparklePrimary: '#46F7F7',
    sparkleSecondary: '#8AFFFC'
  },
  red: {
    name: 'red',
    ariaLabel: 'Red player home base',
    surfacePrimary: '#E43C0B',
    surfaceSecondary: '#F84D00',
    accentStart: '#F98403',
    accentMid: '#FFB408',
    accentEnd: '#F98403',
    pawnGradientStart: '#F98403',
    pawnGradientMid: '#FFB408',
    pawnGradientEnd: '#F98403',
    pawnSpotShadow: '#3A1200',
    sparklePrimary: '#F6C634',
    sparkleSecondary: '#F5E48C'
  },
  yellow: {
    name: 'yellow',
    ariaLabel: 'Yellow player home base',
    surfacePrimary: '#FFCE00',
    surfaceSecondary: '#FFF810',
    accentStart: '#FFCE00',
    accentMid: '#FFF810',
    accentEnd: '#FFCE00',
    pawnGradientStart: '#FFCE00',
    pawnGradientMid: '#FFF810',
    pawnGradientEnd: '#FFCE00',
    pawnSpotShadow: '#3A3000',
    sparklePrimary: '#F6C634',
    sparkleSecondary: '#F5E48C'
  },
  green: {
    name: 'green',
    ariaLabel: 'Green player home base',
    surfacePrimary: '#128406',
    surfaceSecondary: '#2BBF02',
    accentStart: '#29B208',
    accentMid: '#7DFF0B',
    accentEnd: '#25C909',
    pawnGradientStart: '#29B208',
    pawnGradientMid: '#7DFF0B',
    pawnGradientEnd: '#25C909',
    pawnSpotShadow: '#042800',
    sparklePrimary: '#63F943',
    sparkleSecondary: '#8AFFFC'
  }
} as const satisfies Record<string, MagmaBaseColorScheme>;
